import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import PageWrap from '../../../components/auth/page-wrap';
import { getErrorMessage, getErrorStatus, getFieldErrors } from '../../../core/api/response-handler';
import { applyServerFieldErrors } from '../../../shared/utils/errors';
import { inregistreazaCodOtp } from '../api/otp.api';
import { reduceTrimitere, STARE_TRIMITERE_INITIALA } from '../utils/stare-trimitere';
import { elevCodSchema, type ElevCodFormData } from '../validation/registration.schemas';
import { trackSignupFormFailed, trackSignupFormSubmitted } from '@/telemetry/posthog-events';

/**
 * Inscrierea ELEVULUI prin cod, fara parola (V1c, lansarea 21.09).
 *
 * Perechea de elev a lui `InregistrareCodPage`: acolo se inscrie profesorul, aici elevul, pe
 * aceeasi ruta din backend, cu `rol: 'ELEV'`.
 *
 * 🔴 <b>Ce NU promite ecranul asta.</b> Ruta raspunde 200 cu acelasi mesaj si cand nu s-a intamplat
 * nimic: un e-mail care are deja cont de elev in editia curenta, sau care apartine unui profesor,
 * cade pe o ramura tacuta, deliberat (altfel raspunsul ar spune cine are cont). Deci ecranul
 * urmator poate astepta un cod care nu vine, iar textul de aici n-are voie sa afirme ca s-a trimis
 * ceva. De-aia scrie «dacă datele sunt corecte», nu «ți-am trimis codul».
 *
 * ⚠️ Refuzurile care se VAD sunt altceva, si vin ca 400 cu harta de campuri: cod de profesor
 * invalid, clasa necompletata. Alea sunt despre ce a trimis omul, deci se randeaza pe campul lor
 * prin `applyServerFieldErrors` — nu intr-un toast generic, unde ar fi imposibil de legat de camp.
 *
 * ⚠️ Codul de profesor nu se valideaza pe forma in frontend: backendul accepta si codul SE al
 * scolii, iar o regula ghicita aici ar respinge coduri valide inainte sa plece cererea.
 */
export default function InregistrareElevCodPage() {
    const { PATHS, PAGES } = useRoutesProvider();
    const navigate = useNavigate();
    const [{ seTrimite, eroare }, dispatch] = React.useReducer(reduceTrimitere, STARE_TRIMITERE_INITIALA);

    const form = useForm<ElevCodFormData>({
        resolver: zodResolver(elevCodSchema),
        defaultValues: { nume: '', prenume: '', email: '', telefon: '', clasa: '', codProfesor: '' },
        mode: 'onBlur',
    });

    async function trimite(date: ElevCodFormData) {
        if (seTrimite) return;
        dispatch({ tip: 'pleaca' });
        try {
            const mesaj = await inregistreazaCodOtp({
                nume: date.nume,
                prenume: date.prenume,
                email: date.email,
                // Gol inseamna „nu l-a dat": se trimite `undefined`, nu `''`, ca backendul sa vada
                // un camp absent, nu unul completat cu nimic.
                telefon: date.telefon?.trim() ? date.telefon.trim() : undefined,
                rol: 'ELEV',
                codProfesor: date.codProfesor,
                clasa: date.clasa,
            });
            trackSignupFormSubmitted('ELEV');
            navigate(PATHS[PAGES.VERIFICARE_COD], {
                state: { identificator: date.email, cerutLa: Date.now(), mesaj, flux: 'inregistrare' },
            });
        } catch (err) {
            const campuri = getFieldErrors(err);
            trackSignupFormFailed({
                role: 'ELEV',
                errorType: 'server',
                httpStatus: getErrorStatus(err),
                errorFields: campuri ? Object.keys(campuri) : undefined,
            });
            // Intai campurile, apoi mesajul general: un 400 pe `codProfesor` trebuie sa se vada
            // LANGA camp. `applyServerFieldErrors` citeste harta `errors` din `ApiValidationError`,
            // adica exact cheile pe care le pune `OtpInregistrareController.cerereInvalida`.
            applyServerFieldErrors(err, form.setError);
            dispatch({ tip: 'esuat', mesaj: getErrorMessage(err, 'Nu am putut trimite codul. Încearcă din nou.') });
        } finally {
            dispatch({ tip: 'terminat' });
        }
    }

    /* Alerta veche se goleste la APASARE, nu in `trimite()`: acolo se ajunge abia dupa ce validarea
       de client a trecut, iar o trimitere respinsa de client lasa pe ecran o eroare de SERVER care
       nu mai are legatura cu ce tocmai s-a intamplat (V1, 2026-09-18). */
    const laTrimitere = (e: React.FormEvent<HTMLFormElement>) => {
        dispatch({ tip: 'incearca' });
        return form.handleSubmit(trimite, laInvalid)(e);
    };

    function laInvalid(erori: FieldErrors<ElevCodFormData>) {
        trackSignupFormFailed({ role: 'ELEV', errorType: 'client', errorFields: Object.keys(erori) });
    }

    return (
        <PageWrap>
            <Card className="mx-auto mb-4 w-full max-w-md">
                <CardHeader>
                    <CardTitle as="h1" className="font-heading">Înscrie-te ca elev</CardTitle>
                    <CardDescription>
                        Dacă datele sunt corecte, primești un cod unic pe e-mail. Nu ai nevoie de parolă.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={laTrimitere} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="prenume"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Prenume:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="text" autoComplete="given-name"
                                                       className="form-input-styled" aria-required="true" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="nume"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Nume:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="text" autoComplete="family-name"
                                                       className="form-input-styled" aria-required="true" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {/* `type="email"`, obligatoriu — un selector care cauta type-ul rateaza
                                tacut un camp fara atribut (P1-4, valul p7). */}
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Email:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="email" autoComplete="email"
                                                       autoCapitalize="none" autoCorrect="off" spellCheck={false}
                                                       placeholder="exemplu@email.ro"
                                                       className="form-input-styled" aria-required="true" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="telefon"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        {/* «(opțional)» in ETICHETA, nu sub camp: altfel omul completeaza
                                            intai si citeste dupa. */}
                                        <FormLabel className="form-label-bold">Telefon (opțional):</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="tel" inputMode="tel" autoComplete="tel"
                                                       className="form-input-styled" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="clasa"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Clasa:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="text" placeholder="ex. 5A"
                                                       className="form-input-styled" aria-required="true" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="codProfesor"
                                render={({ field }) => (
                                    <FormItem className="form-field-row">
                                        <FormLabel className="form-label-bold">Codul profesorului:</FormLabel>
                                        <div className="flex-1 space-y-0">
                                            <FormControl>
                                                <Input {...field} type="text" autoCapitalize="characters"
                                                       autoCorrect="off" spellCheck={false}
                                                       className="form-input-styled" aria-required="true" />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                            <p className="m-0 mt-1 text-sm text-gray-600">
                                                Îl primești de la profesorul coordonator al școlii tale.
                                            </p>
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {eroare && (
                                <p role="alert" data-eroare-inscriere
                                   className="m-0 rounded-lg border border-error bg-error/10 p-3 text-small text-error">
                                    {eroare}
                                </p>
                            )}

                            <Button type="submit" size="lg" disabled={seTrimite} className="w-full touch-target">
                                {seTrimite ? 'Se trimite…' : 'Trimite-mi codul'}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </PageWrap>
    );
}
